// v10/src/components/admin/inventory/InventoryItemsList.js
//
// Listado "tonto" de items del inventario (A8) — Tarea 1 (lectura).
// Recibe la lista ya filtrada y la renderiza, con loading/error/empty states
// y, opcionalmente, agrupando por categoría.

import React, { useMemo } from 'react';
import { Loader, AlertCircle, PackageOpen, Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import InventoryItemCard from './InventoryItemCard';
import { CATEGORY_KEYS, getCategoryIcon } from './categoryIcons';

const InventoryItemsList = ({
  items,
  loading,
  error,
  groupBy = null,                 // 'category' | null
  emptyVariant = 'no_items',       // 'no_items' | 'no_results'
  onClearFilters,
  onRetry,
  darkMode,
  locale = 'es-AR',
  onItemAction,
  canEdit = true,
  canDelete = true,
}) => {
  const { t } = useTranslation('admin');

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.32)' : '#94a3b8';

  // Agrupación por categoría (estable según CATEGORY_KEYS).
  const groupedByCategory = useMemo(() => {
    if (groupBy !== 'category') return null;
    const buckets = new Map();
    (items || []).forEach((it) => {
      const k = it.category || 'other';
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(it);
    });
    // Ordenar las categorías según CATEGORY_KEYS (consistente con los filtros).
    const ordered = [];
    CATEGORY_KEYS.forEach((k) => {
      if (buckets.has(k)) ordered.push({ key: k, items: buckets.get(k) });
    });
    // Por si el backend devuelve alguna categoría desconocida, la agregamos al final.
    buckets.forEach((arr, k) => {
      if (!CATEGORY_KEYS.includes(k)) ordered.push({ key: k, items: arr });
    });
    return ordered;
  }, [items, groupBy]);

  // Loading inicial
  if (loading && (!items || items.length === 0) && !error) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          minHeight: 180, color: fgMuted, fontSize: 14,
        }}
      >
        <style>{`@keyframes inventoryListSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
        <Loader style={{ width: 18, height: 18, animation: 'inventoryListSpin 1s linear infinite' }} />
        {t('inventory_loading')}
      </div>
    );
  }

  // Error con retry
  if (error) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 16px', borderRadius: 14,
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)',
          color: '#dc2626', fontSize: 13, fontWeight: 600,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
          {error}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: '6px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: '#dc2626', color: 'white',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
            }}
          >
            {t('inventory_retry')}
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (!items || items.length === 0) {
    const isFiltered = emptyVariant === 'no_results';
    const Icon = isFiltered ? Inbox : PackageOpen;
    const title = isFiltered
      ? t('inventory_empty_state_no_results_title')
      : t('inventory_empty_state_no_items_title');
    const desc = isFiltered
      ? t('inventory_empty_state_no_results_desc')
      : t('inventory_empty_state_no_items_desc');
    return (
      <div
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 14, padding: '48px 24px',
          borderRadius: 20,
          border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
          background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 56, height: 56, borderRadius: 18,
            background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon style={{ width: 26, height: 26, color: '#6366f1' }} />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: fgPrimary }}>{title}</p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 460 }}>{desc}</p>
        </div>
        {isFiltered && onClearFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            style={{
              padding: '9px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              color: 'white', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              boxShadow: '0 6px 16px rgba(79,70,229,0.28)',
            }}
          >
            {t('inventory_filters_clear')}
          </button>
        )}
      </div>
    );
  }

  // Lista — agrupada o flat
  if (groupBy === 'category' && groupedByCategory) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {groupedByCategory.map(({ key, items: groupItems }) => {
          const Icon = getCategoryIcon(key);
          const label = t(`inventory_categories_${key}`, { defaultValue: key });
          return (
            <section key={key}>
              <header
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: darkMode ? 'rgba(99,102,241,0.16)' : 'rgba(99,102,241,0.10)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon style={{ width: 16, height: 16, color: '#6366f1' }} />
                </div>
                <h3
                  style={{
                    margin: 0, fontSize: 14, fontWeight: 800, color: fgPrimary,
                    letterSpacing: '-0.2px',
                  }}
                >
                  {label}
                </h3>
                <span
                  style={{
                    fontSize: 12, fontWeight: 700, color: fgFaint,
                    padding: '2px 9px', borderRadius: 999,
                    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                  }}
                >
                  {groupItems.length}
                </span>
              </header>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 14,
                }}
              >
                {groupItems.map((item) => (
                  <InventoryItemCard
                    key={item.id}
                    item={item}
                    darkMode={darkMode}
                    locale={locale}
                    onAction={onItemAction}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 14,
      }}
    >
      {items.map((item) => (
        <InventoryItemCard
          key={item.id}
          item={item}
          darkMode={darkMode}
          locale={locale}
          onAction={onItemAction}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      ))}
    </div>
  );
};

export default InventoryItemsList;
